import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { Form, Button, Card, Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { GoogleOAuthProvider, GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { login, clearError, setError, setCredentials } from '../../store/slices/authSlice';
import { RootState, AppDispatch } from '../../store';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { postAuthGoogle } from '../../api/generated/arrayscapeGamingDashboardAPI';

const LoginForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember_me: false,
  });

  // Fetch company name from public settings (unauthenticated)
  const { data: settings } = usePublicSettings();
  const companyName = settings?.companyName || 'Company'; // Default fallback

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(clearError());
    dispatch(login(formData));
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      dispatch(clearError());
      const response = await postAuthGoogle({ id_token: credentialResponse.credential || '' });

      if (response.access_token && response.user) {
        // Store tokens
        localStorage.setItem('token', response.access_token);
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token);
        }

        // Update Redux state
        dispatch(setCredentials({
          user: response.user,
          token: response.access_token
        }));
      }
    } catch (error: any) {
      console.error('Google login failed:', error);
      // Extract error message from API response
      const errorMessage = error.response?.data?.message || error.message || 'Google login failed';
      dispatch(setError(errorMessage));
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    dispatch(setError('Google sign-in was cancelled or failed'));
  };

  if (isAuthenticated) {
    // Get the page they were trying to access before login
    const from = (location.state as any)?.from?.pathname || '/dashboard';
    return <Navigate to={from} state={{ from: location.state?.from }} replace />;
  }

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-body-tertiary">
        <Row className="w-100">
          <Col xs={12} sm={10} md={8} lg={6} xl={5} className="mx-auto">
            <Card className="rounded-3">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold text-primary mb-2">{companyName} Dashboard</h2>
                  <p className="text-muted">Sign in to your account</p>
                </div>

                {error && (
                  <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="email">Email address</Form.Label>
                  <Form.Control
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                    className="py-3"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label htmlFor="password">Password</Form.Label>
                  <Form.Control
                    id="password"
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter your password"
                    required
                    disabled={loading}
                    className="py-3"
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Check
                    type="checkbox"
                    id="remember_me"
                    name="remember_me"
                    label="Remember me"
                    checked={formData.remember_me}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </Form.Group>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="py-3 fw-semibold"
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </div>
              </Form>

              <div className="position-relative my-4">
                <hr />
                <div className="position-absolute top-50 start-50 translate-middle bg-body px-3">
                  <small className="text-muted">OR</small>
                </div>
              </div>

              <div className="d-grid">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  useOneTap={false}
                  type="standard"
                  size="large"
                  theme="outline"
                  shape="rectangular"
                  logo_alignment="left"
                />
              </div>

              <div className="text-center mt-4">
                <small className="text-muted">
                  Forgot your password? Contact your administrator.
                </small>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </GoogleOAuthProvider>
  );
};

export default LoginForm;
